"use client";

import { Monitor, Moon, Sun } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";

import { useHydrated } from "@/hooks/use-hydrated";
import { cn } from "@/lib/utils";

// =============================================================================

const THEMES = [
  { value: "system", icon: Monitor, labelKey: "themeSystem" },
  { value: "light", icon: Sun, labelKey: "themeLight" },
  { value: "dark", icon: Moon, labelKey: "themeDark" },
] as const;

// =============================================================================

/**
 * Cycles system → light → dark. The icon morphs between states.
 */
export function ThemeSwitcher({ className }: { className?: string }) {
  const t = useTranslations("Preferences");
  const { theme = "system", setTheme } = useTheme();
  const hydrated = useHydrated();

  const index = Math.max(
    0,
    THEMES.findIndex((item) => item.value === theme)
  );
  const current = THEMES[hydrated ? index : 0];
  const next = THEMES[(index + 1) % THEMES.length];
  const Icon = current.icon;

  return (
    <button
      type="button"
      onClick={() => setTheme(next.value)}
      aria-label={`${t("theme")}: ${t(current.labelKey)}`}
      title={`${t("theme")}: ${t(current.labelKey)}`}
      className={cn(
        "relative grid size-9 place-items-center rounded-full text-foreground/80 transition-colors hover:bg-foreground/5 hover:text-foreground",
        className
      )}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={current.value}
          initial={{ rotate: -90, scale: 0.4, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: 90, scale: 0.4, opacity: 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 26 }}
          className="grid place-items-center"
        >
          <Icon className="size-[1.0625rem]" strokeWidth={1.75} />
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

// =============================================================================
